import { useI18n } from '@/context/I18nProvider';
import { ApiClient } from '@/services/api-client';
import { GetLeadByIDResponse } from '@/services/types/global.types';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

// Conversations the user turned the translation on for, remembered per browser.
const STORAGE_KEY = 'orsay-translated-conversations';
const MAX_REMEMBERED = 200;
// Per request; the backend accepts up to 300 messages.
const MAX_MESSAGES_PER_REQUEST = 300;
const DEBOUNCE_MS = 400;

// Messages with nothing to translate (voice-note placeholder).
const SKIPPED_TEXTS = new Set(['', '🔊']);
export type TranslatableMessage = {
  text: string;
  inbound: boolean;
};
const readEnabled = (): string[] => {
  try {
    const value = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
};
const writeEnabled = (leadIds: string[]) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(leadIds.slice(-MAX_REMEMBERED)));
  } catch {
    // Storage unavailable (private mode…): the toggle just won't be remembered.
  }
};
export const languageDisplayName = (code: string | null | undefined, locale: string) => {
  if (!code) return '';
  try {
    const name = new Intl.DisplayNames([locale], {
      type: 'language'
    }).of(code) ?? code;
    return name.charAt(0).toUpperCase() + name.slice(1);
  } catch {
    return code;
  }
};

/**
 * Translation of a conversation into the user's language, for a salesperson
 * who doesn't speak the lead's language. Manual: nothing is translated until
 * the user turns it on for the conversation. Each message is only sent once
 * to the backend, which caches the translations.
 */
export const useConversationTranslation = ({
  leadId,
  leadLanguage,
  messages
}: {
  leadId: string;
  leadLanguage?: string | null;
  messages: TranslatableMessage[];
}) => {
  const {
    t,
    locale
  } = useI18n();
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(false);
  const [translations, setTranslations] = useState<Map<string, string>>(new Map());
  const [isTranslating, setIsTranslating] = useState(false);
  // Texts already asked for (or that failed): never sent twice in a session.
  const requested = useRef<Set<string>>(new Set());
  const justEnabled = useRef(false);
  useEffect(() => {
    setEnabled(readEnabled().includes(leadId));
    setTranslations(new Map());
    requested.current = new Set();
  }, [leadId]);
  const sameLanguage = !!leadLanguage && leadLanguage === locale;
  const setEnabledForLead = useCallback((value: boolean) => {
    const others = readEnabled().filter(id => id !== leadId);
    writeEnabled(value ? [...others, leadId] : others);
    setEnabled(value);
  }, [leadId]);
  const toggle = useCallback(() => {
    justEnabled.current = !enabled;
    if (!enabled) requested.current = new Set();
    setEnabledForLead(!enabled);
  }, [enabled, setEnabledForLead]);
  const missing = useMemo(() => {
    if (!enabled) return [];
    const seen = new Set<string>();
    return messages.filter(message => {
      const text = message.text.trim();
      if (SKIPPED_TEXTS.has(text) || translations.has(text) || seen.has(text)) return false;
      if (requested.current.has(text)) return false;
      seen.add(text);
      return true;
    });
  }, [enabled, messages, translations]);
  useEffect(() => {
    if (!missing.length) return;
    const batch = missing.slice(0, MAX_MESSAGES_PER_REQUEST).map(message_0 => ({
      text: message_0.text.trim(),
      inbound: message_0.inbound
    }));
    const timer = setTimeout(async () => {
      batch.forEach(message_1 => requested.current.add(message_1.text));
      setIsTranslating(true);
      try {
        const {
          data
        } = await ApiClient.lead.translateConversation({
          leadId,
          targetLanguage: locale,
          messages: batch
        });
        setTranslations(previous => {
          const next = new Map(previous);
          data.Translations.forEach(item => next.set(item.Text.trim(), item.Translation));
          return next;
        });
        if (data.LeadLanguage) {
          queryClient.setQueryData<GetLeadByIDResponse>(['lead.getLead', {
            id: leadId
          }], old => old?.data ? {
            ...old,
            data: {
              ...old.data,
              LeadLanguage: data.LeadLanguage
            }
          } : old);
        }
        if (justEnabled.current && data.LeadLanguage === locale) {
          toast.info(t('conversations.translation.sameLanguage', {
            language: languageDisplayName(locale, locale)
          }));
          setEnabledForLead(false);
        }
      } catch {
        toast.error(t('conversations.translation.error'));
      } finally {
        justEnabled.current = false;
        setIsTranslating(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [missing, leadId, locale, queryClient, setEnabledForLead, t]);

  /** The translation to show for a message, if it differs from the original. */
  const translationFor = useCallback((text_0: string | undefined) => {
    if (!enabled || !text_0) return undefined;
    const translation = translations.get(text_0.trim());
    return translation && translation.trim() !== text_0.trim() ? translation : undefined;
  }, [enabled, translations]);

  /** A reply sent through the translation: we already know what it says. */
  const rememberTranslation = useCallback((translated: string, original: string) => {
    requested.current.add(translated.trim());
    setTranslations(previous_0 => new Map(previous_0).set(translated.trim(), original));
  }, []);
  return {
    enabled,
    toggle,
    isTranslating,
    sameLanguage,
    translationFor,
    rememberTranslation,
    /** Replies are translated before sending only once the lead's language is known. */
    canTranslateReplies: enabled && !!leadLanguage && !sameLanguage
  };
};